import React, { useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import {
    addDoc,
    collection,
    doc,
    getDoc,
    increment,
    onSnapshot,
    orderBy,
    serverTimestamp,
    setDoc,
    updateDoc,
    query,
} from "firebase/firestore";
import { db } from "../../firebase/config";
import { useAuth } from "../../contexts/AuthContext";
import { getErrorMessage } from "../../utils/errorUtils";
import "./ChatInquiry.css";

const ChatInquiry = ({ isOpen, onClose }) => {
    const { currentUser, getUserProfile } = useAuth();
    const [chatId,      setChatId]      = useState(null);
    const [messages,    setMessages]    = useState([]);
    const [messageText, setMessageText] = useState("");
    const [loading,     setLoading]     = useState(false);
    const [sending,     setSending]     = useState(false);
    const [actionError, setActionError] = useState(null);

    // Track whether the chat doc exists in Firestore yet
    const [chatExists,  setChatExists]  = useState(false);
    // Cache user profile for use in handleSendMessage
    const userNameRef = useRef(null);

    const messagesEndRef = useRef(null);
    const inputRef       = useRef(null);
    const location       = useLocation();

    // Close chat when navigating to a different page
    useEffect(() => {
        if (isOpen) onClose();
    }, [location.pathname]);

    const canChat = !!currentUser?.uid;

    const displayName = useMemo(() => {
        if (!currentUser) return "Customer";
        if (currentUser.displayName?.trim()) return currentUser.displayName.trim();
        return currentUser.email || "Customer";
    }, [currentUser]);

    // ── Auto-scroll to bottom whenever messages change ────────────────────────
    useEffect(() => {
        messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
    }, [messages]);

    // ── Focus input when widget opens ─────────────────────────────────────────
    useEffect(() => {
        if (isOpen && inputRef.current) {
            setTimeout(() => inputRef.current?.focus(), 120);
        }
    }, [isOpen]);

    // ── Setup chat + subscribe to messages ────────────────────────────────────
    // KEY CHANGE: We no longer create the chat doc here on open.
    // We only check if it exists and subscribe to messages if it does.
    // The chat doc is created lazily on first message send.
    useEffect(() => {
        if (!isOpen || !canChat) return undefined;

        let isMounted           = true;
        let unsubscribeMessages = null;

        const setupChat = async () => {
            setLoading(true);
            try {
                const activeChatId  = currentUser.uid;
                const activeChatRef = doc(db, "chats", activeChatId);
                const activeChatSnap = await getDoc(activeChatRef);

                // Resolve display name for later use when creating the chat doc
                const userProfile = await getUserProfile(currentUser.uid);
                const userName =
                    [userProfile?.firstName, userProfile?.lastName]
                        .filter(Boolean).join(" ").trim() || displayName;
                userNameRef.current = userName;

                if (!isMounted) return;

                if (activeChatSnap.exists()) {
                    // Chat doc already exists — update name/email and subscribe
                    setChatExists(true);
                    setChatId(activeChatId);

                    await setDoc(
                        activeChatRef,
                        {
                            userName,
                            userEmail: currentUser.email || "",
                            status:    "active",
                            updatedAt: serverTimestamp(),
                        },
                        { merge: true }
                    );

                    const messagesQuery = query(
                        collection(db, "chats", activeChatId, "messages"),
                        orderBy("timestamp", "asc")
                    );

                    unsubscribeMessages = onSnapshot(messagesQuery, async (snapshot) => {
                        if (!isMounted) return;

                        const nextMessages = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
                        setMessages(nextMessages);

                        const markPromises = snapshot.docChanges()
                            .filter((c) =>
                                c.type === "added" &&
                                c.doc.data().senderType === "admin" &&
                                !c.doc.data().readByCustomer
                            )
                            .map((c) =>
                                updateDoc(
                                    doc(db, "chats", activeChatId, "messages", c.doc.id),
                                    { readByCustomer: true }
                                )
                            );

                        if (markPromises.length > 0) await Promise.all(markPromises);

                        await updateDoc(doc(db, "chats", activeChatId), {
                            unreadByCustomer: 0,
                            updatedAt:        serverTimestamp(),
                        });
                    }, (err) => {
                        console.warn("ChatInquiry messages snapshot error:", err.message);
                    });

                } else {
                    // Chat doc does NOT exist yet — don't create it.
                    // It will be created when the customer sends their first message.
                    setChatExists(false);
                    setChatId(activeChatId); // store ID for use in handleSendMessage
                }

            } catch (error) {
                console.error("Error setting up chat:", error);
            } finally {
                if (isMounted) setLoading(false);
            }
        };

        setupChat();

        return () => {
            isMounted = false;
            if (typeof unsubscribeMessages === "function") unsubscribeMessages();
        };
    }, [isOpen, canChat, currentUser, displayName, getUserProfile]);

    // ── Send message ──────────────────────────────────────────────────────────
    const handleSendMessage = async () => {
        if (!chatId || !messageText.trim() || sending) return;
        const textToSend = messageText.trim();
        setSending(true);
        try {
            const chatRef = doc(db, "chats", chatId);

            // If chat doc doesn't exist yet, create it now (first message)
            if (!chatExists) {
                const userName = userNameRef.current || displayName;
                await setDoc(chatRef, {
                    userId:           currentUser.uid,
                    userName,
                    userEmail:        currentUser.email || "",
                    status:           "active",
                    lastMessage:      "",
                    lastMessageBy:    null,
                    unreadByAdmin:    0,
                    unreadByCustomer: 0,
                    createdAt:        serverTimestamp(),
                    updatedAt:        serverTimestamp(),
                    lastMessageTime:  serverTimestamp(),
                });
                setChatExists(true);

                // Now subscribe to messages since the doc exists
                const messagesQuery = query(
                    collection(db, "chats", chatId, "messages"),
                    orderBy("timestamp", "asc")
                );
                onSnapshot(messagesQuery, async (snapshot) => {
                    const nextMessages = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
                    setMessages(nextMessages);

                    const markPromises = snapshot.docChanges()
                        .filter((c) =>
                            c.type === "added" &&
                            c.doc.data().senderType === "admin" &&
                            !c.doc.data().readByCustomer
                        )
                        .map((c) =>
                            updateDoc(
                                doc(db, "chats", chatId, "messages", c.doc.id),
                                { readByCustomer: true }
                            )
                        );

                    if (markPromises.length > 0) await Promise.all(markPromises);

                    await updateDoc(doc(db, "chats", chatId), {
                        unreadByCustomer: 0,
                        updatedAt:        serverTimestamp(),
                    });
                }, (err) => {
                    console.warn("ChatInquiry messages snapshot error:", err.message);
                });
            }

            await addDoc(collection(db, "chats", chatId, "messages"), {
                senderId:       currentUser.uid,
                senderType:     "customer",
                senderName:     displayName,
                messageType:    "text",
                messageText:    textToSend,
                imageUrl:       null,
                readByAdmin:    false,
                readByCustomer: true,
                timestamp:      serverTimestamp(),
            });

            await updateDoc(chatRef, {
                lastMessage:     textToSend,
                lastMessageBy:   "customer",
                lastMessageTime: serverTimestamp(),
                unreadByAdmin:   increment(1),
                updatedAt:       serverTimestamp(),
            });

            setMessageText("");
        } catch (error) {
            console.error("Error sending message:", error);
            setActionError(getErrorMessage(error, "Failed to send message. Please try again."));
        } finally {
            setSending(false);
        }
    };

    const handleKeyDown = (e) => {
        if (e.key === "Enter" && !e.shiftKey) {
            e.preventDefault();
            handleSendMessage();
        }
    };

    function formatTime(timestamp) {
        if (!timestamp) return "";
        const d = timestamp?.toDate ? timestamp.toDate() : new Date(timestamp);
        if (isNaN(d.getTime())) return "";
        return d.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
    }

    if (!isOpen) return null;

    return (
        <div className="chat-widget">

            {/* ── Header ── */}
            <div className="ci-header">

                {/* Back arrow — mobile only */}
                <button className="chat-back" onClick={onClose} aria-label="Go back">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="15 18 9 12 15 6"/>
                    </svg>
                </button>

                {/* Avatar */}
                <div className="ci-header-avatar">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4zm0 2c-4 0-7 2-7 4v2h14v-2c0-2-3-4-7-4z"/>
                    </svg>
                </div>

                {/* Name + status */}
                <div className="ci-header-info">
                    <span className="ci-header-name">JG Printing</span>
                    <span className="ci-header-status">
                        <span className="ci-status-dot" />
                        Support Team
                    </span>
                </div>

                {/* Close — desktop only */}
                <button className="ci-close-btn" onClick={onClose} aria-label="Close chat">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                        <line x1="18" y1="6" x2="6" y2="18"/>
                        <line x1="6" y1="6" x2="18" y2="18"/>
                    </svg>
                </button>

            </div>

            {/* ── Body ── */}
            <div className="ci-body">
                {actionError && (
                    <div className="ci-error-banner" onClick={() => setActionError(null)}>
                        {actionError}
                    </div>
                )}
                {!canChat ? (
                    <div className="ci-empty-state">
                        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5">
                            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
                        </svg>
                        <p>Please <strong>log in</strong> to start a conversation with our team.</p>
                    </div>
                ) : loading ? (
                    <div className="ci-empty-state">
                        <div className="ci-loading-dots">
                            <span /><span /><span />
                        </div>
                        <p>Connecting…</p>
                    </div>
                ) : messages.length === 0 ? (
                    <div className="ci-empty-state">
                        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#ccc" strokeWidth="1.5">
                            <path d="M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z"/>
                        </svg>
                        <p>No messages yet. Send us a message and we'll get back to you!</p>
                    </div>
                ) : (
                    messages.map((msg) => {
                        const isCustomer = msg.senderType === "customer";
                        return (
                            <div
                                key={msg.id}
                                className={`ci-message-row ${isCustomer ? "ci-row-customer" : "ci-row-admin"}`}
                            >
                                {!isCustomer && (
                                    <div className="ci-bubble-avatar">
                                        <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                                            <path d="M12 12a4 4 0 1 0-4-4 4 4 0 0 0 4 4zm0 2c-4 0-7 2-7 4v2h14v-2c0-2-3-4-7-4z"/>
                                        </svg>
                                    </div>
                                )}
                                <div className="ci-bubble-wrap">
                                    <div className={`ci-bubble ${isCustomer ? "ci-bubble-customer" : "ci-bubble-admin"}`}>
                                        {msg.messageText}
                                    </div>
                                    <span className="ci-bubble-time">{formatTime(msg.timestamp)}</span>
                                </div>
                            </div>
                        );
                    })
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* ── Input ── */}
            <div className="ci-input-row">
                <input
                    ref={inputRef}
                    type="text"
                    className="ci-input"
                    placeholder={canChat ? "Write a message…" : "Log in to chat"}
                    value={messageText}
                    onChange={(e) => setMessageText(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={!canChat || loading || sending}
                />
                <button
                    className="ci-send-btn"
                    onClick={handleSendMessage}
                    disabled={!canChat || loading || sending || !messageText.trim()}
                    aria-label="Send"
                >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="22" y1="2" x2="11" y2="13"/>
                        <polygon points="22 2 15 22 11 13 2 9 22 2"/>
                    </svg>
                </button>
            </div>

        </div>
    );
};

export default ChatInquiry;